import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import Alert from "react-s-alert";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import SelectFormInput from "src/components/form/inputs/SelectFormInput/SelectFormInput";
import { GroupTrialItemsBreadcrumbs, TrialGroupItemsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { GroupSubMenu, GroupTrialSubMenu, TrialGroupSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupItemsPageActions from "../../../actions/pages/trialGroupItemsPage";
import * as TrialGroupActions from "../../../actions/trialGroup";
import * as TrialGroupItemActions from "../../../actions/trialGroupItem";
import * as TrialGroupItemCategoryActions from "../../../actions/trialGroupItemCategory";
import * as TrialGroupItemTemplateActions from "../../../actions/trialGroupItemTemplate";
import { InlineLoading, Modal, NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import CollectionInstanceComponent from "../../../containers/Collection/CollectionInstance/CollectionInstanceComponent";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { ModalSize } from "../../../enumerations/ModalSize";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { setModalTitle } from "../../../helpers/modalHelper";
import { ITmdState } from "../../../reducers/index";
import { TrialGroupItemCreateVersion } from "../../index";

import "./TrialGroupItemsPage.scss";

interface TrialGroupItemsPageProps {
    trialGroup: Dtos.TrialGroup;
    loadingTrialGroup: boolean;
    loadTrialGroupSuccess: boolean;
    loadTrialGroupFailure: boolean;
    loadTrialGroupErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    collectionInstanceModalOpen: boolean;
    collectionInstanceId: number;
    modalTitle: string;
    modalDescription: string;
    trialGroupItemTemplates: Dtos.TrialGroupItemTemplate[];
    loadingTrialGroupItemTemplates: boolean;
    loadTrialGroupItemTemplatesSuccess: boolean;
    loadTrialGroupItemTemplatesFailure: boolean;
    loadTrialGroupItemTemplatesErrorCode: string;
    savingTrialGroupItem: boolean;
    saveTrialGroupItemSuccess: boolean;
    createVersionModalOpen: boolean;
    createVersionTrialGroupItem: Dtos.TrialGroupItem;
    trialGroupItemCategories: Dtos.TrialGroupItemCategory[];
    loadingTrialGroupItemCategories: boolean;
    loadTrialGroupItemCategoriesSuccess: boolean;
    loadTrialGroupItemCategoriesFailure: boolean;
    loadTrialGroupItemCategoriesErrorCode: string;
    clearTrialGroup: TrialGroupActions.ITrialGroupClearActionCreator;
    loadTrialGroup: TrialGroupActions.ITrialGroupLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrialGroupItemTemplates: TrialGroupItemTemplateActions.ITrialGroupItemTemplateClearActionCreator;
    loadTrialGroupItemTemplates: TrialGroupItemTemplateActions.ITrialGroupItemTemplateLoadActionCreator;
    saveTrialGroupItem: TrialGroupItemActions.ITrialGroupItemSaveActionCreator;
    setEditModelViewState: TrialGroupItemsPageActions.ITrialGroupItemsPageSetEditModalViewStateActionCreator;
    setCreateVersionModelViewState: TrialGroupItemsPageActions.ITrialGroupItemsPageSetCreateVersionModalViewStateActionCreator;
    clearTrialGroupItemCategories: TrialGroupItemCategoryActions.ITrialGroupItemCategoriesClearActionCreator;
    loadTrialGroupItemCategories: TrialGroupItemCategoryActions.ITrialGroupItemCategoriesLoadByTrialGroupIdActionCreator;
}

const TrialGroupItemsPage: FC<TrialGroupItemsPageProps> = ({
    trialGroup,
    loadingTrialGroup,
    loadTrialGroupSuccess,
    loadTrialGroupFailure,
    loadTrialGroupErrorCode,
    permissions,
    collectionInstanceModalOpen,
    collectionInstanceId,
    modalTitle,
    modalDescription,
    trialGroupItemTemplates,
    loadingTrialGroupItemTemplates,
    loadTrialGroupItemTemplatesSuccess,
    loadTrialGroupItemTemplatesFailure,
    loadTrialGroupItemTemplatesErrorCode,
    savingTrialGroupItem,
    saveTrialGroupItemSuccess,
    createVersionModalOpen,
    createVersionTrialGroupItem,
    trialGroupItemCategories,
    loadingTrialGroupItemCategories,
    loadTrialGroupItemCategoriesSuccess,
    loadTrialGroupItemCategoriesFailure,
    loadTrialGroupItemCategoriesErrorCode,
    clearTrialGroup,
    loadTrialGroup,
    setBreadcrumbs,
    setDrawerItems,
    clearTrialGroupItemTemplates,
    loadTrialGroupItemTemplates,
    saveTrialGroupItem,
    setEditModelViewState,
    setCreateVersionModelViewState,
    clearTrialGroupItemCategories,
    loadTrialGroupItemCategories
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{ groupId: string }>();
    const { trialId } = useParams<{ trialId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const groupIdNumber = parseInt(groupId, 10);
    const trialIdNumber = parseInt(trialId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);

    //
    // -- States --
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [addTrialGroupItemId, setAddTrialGroupItemId] = useState<number | undefined>(undefined);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const location = useLocation();
    const navigate = useNavigate();

    //
    // -- Variables --
    const showTrialGroupItems =
        !loadingTrialGroup &&
        loadTrialGroupSuccess &&
        !loadingTrialGroupItemTemplates &&
        loadTrialGroupItemTemplatesSuccess &&
        !loadingTrialGroupItemCategories &&
        loadTrialGroupItemCategoriesSuccess &&
        !creatingTree;



    // Load Trial Group, Items & Categories
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        const drawerItemSetter =
            baseRoute === "trials" ? TrialGroupSubMenu : GroupTrialSubMenu;

        const breadcrumbSetter =
            baseRoute === "trials"
                ? TrialGroupItemsBreadcrumbs
                : GroupTrialItemsBreadcrumbs;

        if (
            groupIdNumber &&
            trialIdNumber &&
            trialGroupIdNumber &&
            !trialGroup &&
            !loadingTrialGroupItemTemplates &&
            !loadingTrialGroup
        ) {
            setDrawerItems(
                drawerItemSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber
                )
            );

            loadTrialGroup(trialGroupIdNumber);
            loadTrialGroupItemTemplates(trialGroupIdNumber);
            loadTrialGroupItemCategories(trialGroupIdNumber);
        }
    }, [
        location,
        groupIdNumber,
        trialIdNumber,
        trialGroupIdNumber,
        permissions,
        trialGroup,
        trialGroupItemTemplates,
        setBreadcrumbs,
        setDrawerItems,
        loadTrialGroup,
        TrialGroupItemsBreadcrumbs,
        GroupSubMenu,
        loadTrialGroupItemTemplates,
        loadTrialGroupItemTemplates
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupItemTemplatesFailure) {
            navigate(`/error/${loadTrialGroupItemTemplatesErrorCode}`);
        }
        if (loadTrialGroupFailure) {
            navigate(`/error/${loadTrialGroupErrorCode}`);
        }
        if (loadTrialGroupItemCategoriesFailure) {
            navigate(`/error/${loadTrialGroupItemCategoriesErrorCode}`);
        }
    }, [
        loadTrialGroupItemTemplatesFailure,
        loadTrialGroupFailure,
        loadTrialGroupItemTemplatesErrorCode,
        loadTrialGroupErrorCode,
        loadTrialGroupItemCategoriesFailure,
        loadTrialGroupItemCategoriesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            trialGroup &&
            trialGroup?.trial?.studyAcronym &&
            trialGroup?.group?.name
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupItemsBreadcrumbs
                    : GroupTrialItemsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroup?.trial?.studyAcronym,
                    trialGroup?.group?.name
                )
            );
        }
    }, [
        location,
        trialGroup,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        GroupTrialItemsBreadcrumbs,
        TrialGroupItemsBreadcrumbs,
        setBreadcrumbs,
    ]);

    // Cleanup effect to clear the trial group item on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroup();
            clearTrialGroupItemTemplates();
            clearTrialGroupItemCategories();
        };
    }, [clearTrialGroup, clearTrialGroupItemCategories, clearTrialGroupItemTemplates]);

    // Reload trial group & items after saving group item
    useEffect(() => {
        if (!savingTrialGroupItem && saveTrialGroupItemSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial group item saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );
            loadTrialGroup(trialGroupIdNumber);
            loadTrialGroupItemCategories(trialGroupIdNumber);
        }
    }, [
        savingTrialGroupItem,
        saveTrialGroupItemSuccess,
        trialGroupIdNumber,
        loadTrialGroup,
        loadTrialGroupItemCategories,
    ]);

    // Refresh tree
    useEffect(() => {
        if (trialGroupItemCategories) {
            setInitalizedTreeData(false);
        }
    }, [trialGroupItemCategories, setInitalizedTreeData]);

    const handleModalClose = () => {
        setIsOpen(false);
        setAddTrialGroupItemId(undefined);
    };

    const handleModalSubmit = async () => {
        if (!addTrialGroupItemId || !trialGroupIdNumber) {
            Alert.error(
                <NotificationAlert
                    alertContent={<span>An error occurred saving the group item</span>}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
            return;
        }
        let template = trialGroupItemTemplates.find((x) => x.id === addTrialGroupItemId);
        let trialGroupItem: Dtos.TrialGroupItem = {
            trialGroupId: trialGroupIdNumber,
            trialGroupItemTemplateId: addTrialGroupItemId,
            version: template?.initialVersion ?? null,
            active: true,
        } as Dtos.TrialGroupItem;
        await saveTrialGroupItem(trialGroupItem, null);

        setIsOpen(false);
        setAddTrialGroupItemId(undefined);
    };

    // Create tree
    useEffect(() => {
        if (trialGroupItemCategories && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial group items return
            if (trialGroupItemCategories?.length == 0) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (
                trialGroupItemSummaries: Dtos.TrialGroupItemSummary[]
            ) => {
                let children = [];
                const groupedSummaries =
                    Object.values<Dtos.TrialGroupItemSummary>(
                        trialGroupItemSummaries.reduce((acc, item) => {
                            const key = `${item.trialGroupId}-${item.trialGroupItemTemplateId}`;

                            if (!acc[key] || new Date(item.dateEntered) > new Date(acc[key].dateEntered)) {
                                acc[key] = item;
                            }

                            return acc;
                        }, {})
                    );

                children =
                    groupedSummaries
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `item-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 2fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.FILE} />
                                    </div>
                                    <div className="px-1">{y?.name}</div>
                                    <div className="d-inline-block px-1 text-truncate">
                                        <span
                                            className="text-truncate"
                                            data-tip={y?.description}
                                        >
                                            {y?.description}
                                            <ReactTooltip />
                                        </span>
                                    </div>
                                    <div className="px-1">
                                        {y?.version && (
                                            <button
                                                disabled={
                                                    !AuthenticationHelper.hasPermission(
                                                        permissions,
                                                        Dtos.Permission.TrialGroupItemUpdate,
                                                        null,
                                                        null
                                                    )
                                                }
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm btn-row-version"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (y?.collectionInstanceId) {
                                                        setEditModelViewState(
                                                            true,
                                                            y.collectionInstanceId,
                                                            y?.name,
                                                            y?.description
                                                        );
                                                    }
                                                }}
                                            >
                                                <span>{y.version}</span>
                                            </button>
                                        )}
                                        {y?.trialGroupItemSummaryVersions?.map(
                                            (item, index) => (
                                                <button
                                                    key={`version-button-${item?.version}-${index}`}
                                                    className="btn btn-secondary btn-sm btn-row-version"
                                                    type="button"
                                                    disabled={
                                                        !AuthenticationHelper.hasPermission(
                                                            permissions,
                                                            Dtos.Permission.TrialGroupItemUpdate,
                                                            null,
                                                            null
                                                        )
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (item?.collectionInstanceId) {
                                                            setEditModelViewState(
                                                                true,
                                                                item.collectionInstanceId,
                                                                y?.name,
                                                                y?.description
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <span>{item.version}</span>
                                                </button>
                                            )
                                        )}
                                    </div>
                                    <div className="px-1 d-flex align-items-start justify-content-end">
                                        {y?.allowVersions && (
                                            <button
                                                disabled={false}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const trialGroupItems = trialGroupItemCategories?.flatMap(x => x.trialGroupItems) ?? [];
                                                    setCreateVersionModelViewState(
                                                        true,
                                                        trialGroupItems.find((x) => x.id == y.id)
                                                    );
                                                }}
                                                type="button"
                                                className="btn btn-outline-secondary mr-2 btn-sm"
                                            >
                                                <span>New version</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr 2fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Version</strong>
                                </div>
                                <div className="px-1"></div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                [...trialGroupItemCategories]
                    ?.sort((a, b) => a?.trialGroupItemTemplateCategory?.rank - b?.trialGroupItemTemplateCategory?.rank)
                    ?.map((x) => ({
                        id: `category-${x?.trialGroupItemTemplateCategory?.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ROLE} />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "400px" }}
                                >
                                    {x?.trialGroupItemTemplateCategory?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        { [...new Set(x?.trialGroupItems?.map((x) => x.trialGroupItemTemplateId)),]?.length }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.trialGroupItemSummaries),],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialGroupItemCategories,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                } else if (id.includes("item")) {
                    const trialGroupItems = trialGroupItemCategories?.flatMap(x => x.trialGroupItems) ?? [];
                    const selectedTrialGroupItem: Dtos.TrialGroupItem = trialGroupItems?.find((x) => x.id?.toString() == id.replace("item-", ""));
                    setEditModelViewState(
                        true,
                        selectedTrialGroupItem?.collectionInstanceId,
                        selectedTrialGroupItem?.trialGroupItemTemplate?.name,
                        selectedTrialGroupItem?.trialGroupItemTemplate?.description
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions, trialGroupItemCategories]
    );

    const renderCreateVersionModal = useMemo(() => {
        if (trialGroup && !loadingTrialGroup) {
            return (
                <Modal
                    open={createVersionModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setCreateVersionModelViewState(false, null);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {setModalTitle(undefined, "Create Version")}
                        </h5>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setCreateVersionModelViewState(false, null);
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div
                        className="modal-body"
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                        <TrialGroupItemCreateVersion
                            trialGroupId={trialGroupIdNumber}
                            currentTrialGroupItem={createVersionTrialGroupItem}
                            onClose={() => {
                                setCreateVersionModelViewState(false, null);
                            }}
                        />
                    </div>
                </Modal>
            );
        }
    }, [
        trialGroupIdNumber,
        loadingTrialGroup,
        createVersionModalOpen,
        setCreateVersionModelViewState,
        createVersionTrialGroupItem,
    ]);

    const renderItemModal = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasAnyPermission(
            permissions,
            [Dtos.Permission.TrialGroupItemUpdate]
        );
        const formDisabled = !hasPermission;

        if (collectionInstanceId && collectionInstanceId > 0) {
            return (
                <Modal
                    open={collectionInstanceModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setEditModelViewState(false, null, "", "");
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h5 className="mb-0">
                                {setModalTitle(undefined, modalTitle)}
                            </h5>
                            {modalDescription ? (
                                <small>
                                    {modalDescription}
                                </small>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setEditModelViewState(false, null, "", "");
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body customscrollbar">
                        <CollectionInstanceComponent 
                            collectionInstanceId={collectionInstanceId}
                            readonly={formDisabled}
                            onClose={() => {
                                setEditModelViewState(false, null, "", "");
                            }}
                            onSaved={() => {
                                loadTrialGroupItemCategories(trialGroupIdNumber)
                            }}
                        />
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        collectionInstanceId,
        collectionInstanceModalOpen,
        permissions,
        modalTitle,
        modalDescription,
        trialGroupIdNumber,
        setEditModelViewState,
        loadTrialGroupItemCategories,
    ]);

    const selectOptions = useMemo(() => {
        if (!trialGroupItemTemplates || !trialGroupItemCategories) return [];

        const trialGroupItems = trialGroupItemCategories?.flatMap(x => x.trialGroupItems) ?? [];
        const currentIds = [...new Set(trialGroupItems?.map((x) => x.trialGroupItemTemplateId))];

        var options = trialGroupItemTemplates
            ?.filter((x) => !currentIds.includes(x.id))
            ?.map((x) => ({ value: x.id, label: x?.initialVersion ? `${x?.name} | ${x.initialVersion}` : x?.name }));

        return options?.sort((a, b) => a?.label?.localeCompare(b?.label));
    }, [trialGroupItemTemplates, trialGroupItemCategories]);

    return (
        <RestrictedLayout
            loading={loadingTrialGroup || loadingTrialGroupItemTemplates || loadingTrialGroupItemCategories}
            permission={Dtos.Permission.TrialGroupItemView}
        >
            <div>
                <div className="row">
                    <div className={"col mb-2"}>
                        <h1>Trial Group Items</h1>
                    </div>
                    <div className="col-auto">
                        {AuthenticationHelper.hasAnyPermission(permissions, [
                            Dtos.Permission.TrialGroupItemCreate,
                        ]) && (
                            <>
                                <div className="btn-group">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setIsOpen(true)}
                                    >
                                        Add Item Template
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary dropdown-toggle dropdown-toggle-split"
                                        data-toggle="dropdown"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                    >
                                        <span className="sr-only">
                                            Toggle Dropdown
                                        </span>
                                    </button>
                                    <div className="dropdown-menu dropdown-menu-right">
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() =>
                                                navigate(`/settings/trialgroup/itemtemplates/${groupIdNumber}/template/create`)
                                            }
                                        >
                                            Create Item Template
                                        </a>
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() =>
                                                navigate(`/settings/trialgroup/itemtemplates/${groupIdNumber}`)
                                            }
                                        >
                                            Edit Item Templates
                                        </a>
                                        <div className="dropdown-divider"></div>
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() => setIsOpen(true)}
                                        >
                                            Add Item Template
                                        </a>
                                    </div>
                                </div>
                                <Modal
                                    open={isOpen}
                                    disableCloseOnOverlayClick={true}
                                    onClose={handleModalClose}
                                    size={ModalSize.Lg}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            Add Trial Group Item
                                        </h5>
                                        <button
                                            type="button"
                                            className="close"
                                            aria-label="Close"
                                            onClick={handleModalClose}
                                        >
                                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                                        </button>
                                    </div>
                                    <div className="modal-body p-3">
                                        <div className="form-row pb-2">
                                            <div className="col-12">
                                                {selectOptions?.length === 0 ? (
                                                    <>
                                                        <span>
                                                            No <em>Trial Group Item Templates</em> are available for <strong>{trialGroup?.trial?.displayName}</strong> - <strong>{trialGroup?.group?.name}</strong>.
                                                        </span>
                                                        <br />
                                                        <button
                                                            className="btn btn-primary mr-1 mt-4"
                                                            onClick={() =>
                                                                navigate(`/settings/trialgroup/itemtemplates/${groupIdNumber}/template/create`)
                                                            }
                                                        >
                                                            Create Item Template
                                                        </button>
                                                    </>
                                                ) : (
                                                    <SelectFormInput
                                                        loading={loadingTrialGroupItemTemplates}
                                                        multi={false}
                                                        options={selectOptions}
                                                        clearable={true}
                                                        backspaceRemoves={false}
                                                        name={"addTrialGroupItemId"}
                                                        id={"addTrialGroupItemId"}
                                                        placeholder="Trial Group Item"
                                                        onChange={function (value: any, _options?: any): any {
                                                            setAddTrialGroupItemId(
                                                                value != undefined
                                                                    ? parseInt(value)
                                                                    : undefined
                                                            );
                                                        }}
                                                        value={addTrialGroupItemId}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-row pt-2">
                                            <div className="col-12 d-flex justify-content-end">
                                                {selectOptions?.length > 0 && (
                                                    <button
                                                        className="btn btn-primary mr-1"
                                                        disabled={
                                                            savingTrialGroupItem ||
                                                            selectOptions?.length === 0
                                                        }
                                                        onClick={handleModalSubmit}
                                                    >
                                                        Submit
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-outline-primary ml-1"
                                                    onClick={handleModalClose}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Modal>
                            </>
                        )}
                    </div>
                </div>
                {!showTrialGroupItems ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialGroupItems ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>
                                        There are currently no items to view.
                                    </span>
                                </div>
                            </div>
                        )}
                        {renderItemModal}
                        {renderCreateVersionModal}
                    </>
                ) : null}
            </div>
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupItemsPageProps> => {
    let trialGroup: Dtos.TrialGroup | undefined = !(
        state.trialGroup.formData instanceof Array
    )
        ? state.trialGroup.formData
        : undefined;
    let trialGroupItemCategories: Dtos.TrialGroupItemCategory[] | undefined = (state.trialGroupItemCategory.formData instanceof Array)
            ? state.trialGroupItemCategory.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroup: trialGroup!,
        loadingTrialGroup: state.trialGroup.loadState && state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupSuccess: state.trialGroup.loadState && state.trialGroup.loadState.status == RequestState.Success,
        loadTrialGroupFailure: state.trialGroup.loadState && state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupErrorCode: state.trialGroup.loadState.errorCode,
        trialGroupItemCategories: trialGroupItemCategories!,
        loadingTrialGroupItemCategories: state.trialGroupItemCategory.loadState && state.trialGroupItemCategory.loadState.status == RequestState.Pending,
        loadTrialGroupItemCategoriesSuccess: state.trialGroupItemCategory.loadState && state.trialGroupItemCategory.loadState.status == RequestState.Success,
        loadTrialGroupItemCategoriesFailure: state.trialGroupItemCategory.loadState && state.trialGroupItemCategory.loadState.status == RequestState.Failure,
        loadTrialGroupItemCategoriesErrorCode: state.trialGroupItemCategory.loadState.errorCode,
        permissions: permissions,
        collectionInstanceModalOpen: state.trialGroupItemsPage.editModalOpen,
        collectionInstanceId: state.trialGroupItemsPage.collectionInstanceId,
        modalTitle: state.trialGroupItemsPage.name,
        modalDescription: state.trialGroupItemsPage.description,
        trialGroupItemTemplates: state.trialGroupItemTemplate.formFilteredData!,
        loadingTrialGroupItemTemplates: state.trialGroupItemTemplate.loadState && state.trialGroupItemTemplate.loadState.status == RequestState.Pending,
        loadTrialGroupItemTemplatesSuccess: state.trialGroupItemTemplate.loadState && state.trialGroupItemTemplate.loadState.status == RequestState.Success,
        loadTrialGroupItemTemplatesFailure: state.trialGroupItemTemplate.loadState && state.trialGroupItemTemplate.loadState.status == RequestState.Failure,
        loadTrialGroupItemTemplatesErrorCode: state.trialGroupItemTemplate.loadState.errorCode,
        savingTrialGroupItem: state.trialGroupItem.saveState && state.trialGroupItem.saveState.status == RequestState.Pending,
        saveTrialGroupItemSuccess: state.trialGroupItem.saveState && state.trialGroupItem.saveState.status == RequestState.Success,
        createVersionModalOpen: state.trialGroupItemsPage.createVersionModalOpen,
        createVersionTrialGroupItem: state.trialGroupItemsPage.trialGroupItem,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialGroupItemsPageProps> => {
    return {
        loadTrialGroup: bindActionCreators(TrialGroupActions.LoadTrialGroupById, dispatch),
        clearTrialGroup: bindActionCreators(TrialGroupActions.Clear, dispatch),
        loadTrialGroupItemTemplates: bindActionCreators(TrialGroupItemTemplateActions.LoadTrialGroupItemTemplate, dispatch),
        clearTrialGroupItemTemplates: bindActionCreators(TrialGroupItemTemplateActions.Clear, dispatch),
        saveTrialGroupItem: bindActionCreators(TrialGroupItemActions.SaveTrialGroupItem, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        setEditModelViewState: bindActionCreators(TrialGroupItemsPageActions.setEditModalViewState, dispatch),
        setCreateVersionModelViewState: bindActionCreators(TrialGroupItemsPageActions.setCreateVersionModalViewState, dispatch),
        loadTrialGroupItemCategories: bindActionCreators(TrialGroupItemCategoryActions.LoadTrialGroupItemCategoriesByTrialGroupId, dispatch),
        clearTrialGroupItemCategories: bindActionCreators(TrialGroupItemCategoryActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialGroupItemsPage);
